<script setup>
import { ref } from 'vue';
import Commons from '../../shared/libraries/commons';
const matchData = ref(null);
</script>

<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.MATCH.SHOW_MATCH_DETAIL"
    :handler="(data) => { matchData = data; $refs.dialog.show(); }"
  />
  <DialogComponent ref="dialog" type="fullscreen" hideCloseButton noInnerPadding>
    <div class="h-full pb-24 overflow-auto bg-gray-50" v-if="matchData">
      <div class="mobile-status-padding"></div>

      <!-- Header -->
      <div class="bg-secondary fixed top-0 w-full z-2">
        <div class="mobile-status-padding"></div>
        <div class="px-4 h-24 flex items-center gap-4">
          <button class="w-8 h-8" @click="$refs.dialog.hide()">
            <ColorImg src="/images/icons/arrow-up.svg" class="w-full h-full bg-white -rotate-90 opacity-70" alt="Back" />
          </button>
          <div class="flex-1 text-center">
            <div class="text-white text-2xl font-bold">{{ Commons.time(matchData?.date, 'dddd, D MMM YYYY') }}</div>
            <div class="text-white/80 text-lg font-medium">{{ matchData?.time || '-' }}</div>
          </div>
          <div class="w-8"></div>
        </div>
      </div>

      <div class="pt-24"></div>

      <!-- Score -->
      <div class="bg-secondary pb-6">
        <div class="grid grid-cols-3 p-6 items-center gap-4">
          <div class="flex flex-col items-center gap-3">
            <div class="w-20 h-20 bg-white/30 rounded-full flex items-center justify-center">
              <ImgComponent :src="matchData?.team1?.logo" fallback="/images/match/t-shirt.svg" class="w-14 h-14" alt="" />
            </div>
            <div class="text-white text-center font-bold text-sm truncate max-w-[120px]">{{ matchData?.team1?.name }}</div>
          </div>
          <div class="flex items-center justify-center">
            <div class="bg-white/20 rounded-2xl px-6 py-3 flex items-center gap-4">
              <div class="text-3xl font-bold text-white w-10 text-center">{{ matchData?.team1?.score ?? '' }}</div>
              <div class="text-white/60 text-2xl">-</div>
              <div class="text-3xl font-bold text-white w-10 text-center">{{ matchData?.team2?.score ?? '' }}</div>
            </div>
          </div>
          <div class="flex flex-col items-center gap-3">
            <div class="w-20 h-20 bg-white/30 rounded-full flex items-center justify-center">
              <ImgComponent :src="matchData?.team2?.logo" fallback="/images/match/t-shirt.svg" class="w-14 h-14" alt="" />
            </div>
            <div class="text-white text-center font-bold text-sm truncate max-w-[120px]">{{ matchData?.team2?.name }}</div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="p-6 space-y-6">
        <!-- Info -->
        <div v-if="matchData?.location || matchData?.durationMin" class="bg-white rounded-2xl overflow-hidden">
          <div class="p-4 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-lg bg-secondary-500 flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 class="font-bold text-gray-800 text-lg">Informations du match</h3>
            </div>
          </div>
          <div class="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div v-if="matchData?.location" class="flex flex-col gap-2">
              <div class="flex items-center gap-2 text-gray-500 text-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <span class="font-semibold">Lieu</span>
              </div>
              <div class="font-medium text-gray-800">
                <div v-if="matchData.location.country">{{ matchData.location.country }}</div>
                <div v-if="matchData.location.city">{{ matchData.location.city }}</div>
              </div>
            </div>
            <div v-if="matchData?.durationMin !== undefined" class="flex flex-col gap-2">
              <div class="flex items-center gap-2 text-gray-500 text-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span class="font-semibold">Durée</span>
              </div>
              <div class="font-medium text-gray-800">{{ matchData.durationMin }} minutes</div>
            </div>
            <div v-if="matchData?.league" class="flex flex-col gap-2">
              <div class="flex items-center gap-2 text-gray-500 text-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
                <span class="font-semibold">Compétition</span>
              </div>
              <div class="font-medium text-gray-800 truncate">{{ matchData.league }}</div>
            </div>
          </div>
        </div>

        <!-- Main odds -->
        <!-- <div class="bg-white rounded-2xl overflow-hidden">
          <div class="p-4 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2zm0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 class="font-bold text-gray-800 text-lg">Côtes principales</h3>
            </div>
          </div>
          <div class="p-4 space-y-3">
            <div
              v-for="quoteId in [
                { name: `Victoire de ${matchData?.team1?.name}`, id: '1' },
                { name: 'Match nul', id: 'X' },
                { name: `Victoire de ${matchData?.team2?.name}`, id: '2' }
              ]"
              :key="quoteId.id"
              class="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200"
            >
              <div class="font-medium text-gray-700 truncate flex-1 pr-4 text-sm">{{ quoteId.name }}</div>
              <div class="px-3 py-1 rounded-lg bg-secondary-500 text-white font-bold text-base">
                {{ matchData.quotes?.[quoteId.id] || '-' }}
              </div>
            </div>
          </div>
        </div> -->

        <!-- All odds -->
        <!-- <div v-if="matchData?.allOdds && Object.keys(matchData.allOdds).length" class="bg-white rounded-2xl overflow-hidden">
          <div class="p-4 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M15 14h.01M12 14h.01M15 17h.01M12 17h.01M9 17h.01M12 21a9 9 0 11-9 9 9 9 0 019 0z"></path>
                </svg>
              </div>
              <h3 class="font-bold text-gray-800 text-lg">Toutes les côtes disponibles</h3>
            </div>
          </div>
          <div class="p-4 space-y-2">
            <div
              v-for="(odd, type) in matchData.allOdds"
              :key="type"
              v-show="!/inconnu/i.test(odd.description)"
              class="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200 gap-3"
            >
              <div class="flex-1">
                <div class="font-bold text-gray-800 mb-1">{{ odd.description }}</div>
                <div class="text-xs text-secondary-600 font-medium">{{ type }} • {{ odd.group }}</div>
              </div>
              <div class="px-4 py-2 rounded-lg bg-purple-500 text-white font-bold text-base">
                {{ odd.value }}
              </div>
            </div>
          </div>
        </div> -->

        <!-- Empty -->
        <div v-else class="flex flex-col items-center justify-center py-12">
          <div class="text-gray-400 text-sm">Aucune cote disponible pour ce match</div>
        </div>
      </div>

      <!-- Bouton Déposer bloqué en bas -->
      <div class="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
        <button
          @click.stop="$bus.emit($constants.EVENTS.SERVICES.START, { group: 'recharges', option: 'fund' })"
          class="w-full flex items-center justify-center gap-3 py-4 px-6 text-white font-bold text-lg rounded-xl bg-primary-500 hover:bg-primary-600"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Déposer
        </button>
      </div>
    </div>
  </DialogComponent>
</template>
